import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { Link } from 'react-router-dom';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  HERMES_RESULT_PATHS,
  HERMES_RESULT_TRANSFORMS,
  HERMES_ON_MISSING_PATH,
  type SpaceRole,
  type Space as SpaceDto,
  type FieldDefinition,
  type ItemTypeListItem,
  type HermesResultPath,
  type HermesResultTransform,
  type HermesStandOption,
  type HermesConfigSource,
} from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { listItemTypes, getItemType } from '../item-types/api.ts';
import { updateSpace } from '../spaces/api.ts';
import {
  getHermesConfig,
  upsertHermesConfig,
  deleteHermesConfig,
  testHermesKey,
  getHermesLog,
  listSpaceHermesStands,
  listHermesConfigSources,
  copyHermesConfigFrom,
} from './api.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import type { HermesEstimateLogEntry } from '@lens/shared';

interface HermesConfigTabProps {
  spaceId: string;
  myRole: SpaceRole;
  commonContext: string | null;
  onCommonContextSaved: (space: SpaceDto) => void;
}

const TEXT_FAMILY = new Set(['text', 'textarea', 'markdown', 'url']);

const preStyle: CSSProperties = {
  margin: 0,
  padding: '8px 10px',
  background: '#f6f6f8',
  border: '1px solid #ededf0',
  borderRadius: 6,
  fontSize: 12,
  maxHeight: 280,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

interface ResultRow {
  key: string;
  resultPath: HermesResultPath;
  activitiesFieldSlug: string;
  transform: HermesResultTransform;
}

let rowSeq = 0;
const newRow = (): ResultRow => ({
  key: `row-${rowSeq++}`,
  resultPath: 'estimate.total.realistic',
  activitiesFieldSlug: '',
  transform: 'number',
});

// Канонический шаблон раскладки результата Hermes. Целевые поля Lens admin
// назначает сам (slug пуст) — у каждого типа записи свои поля.
const TEMPLATE_ROWS: Array<{ resultPath: HermesResultPath; transform: HermesResultTransform }> = [
  { resultPath: 'estimate.total.realistic', transform: 'number' },
  { resultPath: 'estimate.total.optimistic', transform: 'number' },
  { resultPath: 'estimate.total.pessimistic', transform: 'number' },
  { resultPath: 'estimate.by_role.backend', transform: 'number' },
  { resultPath: 'estimate.by_role.frontend', transform: 'number' },
  { resultPath: 'estimate.by_role.analytics', transform: 'number' },
  { resultPath: 'estimate.by_role.testing', transform: 'number' },
  { resultPath: 'task_class', transform: 'text' },
  { resultPath: 'summary', transform: 'text' },
  { resultPath: 'solution.steps', transform: 'join_list' },
  { resultPath: 'risks', transform: 'join_list' },
];

export function HermesConfigTab({
  spaceId,
  myRole,
  commonContext,
  onCommonContextSaved,
}: HermesConfigTabProps): JSX.Element {
  const readOnly = myRole !== 'admin';
  const { user } = useAuth();
  const isPlatformAdmin = Boolean(user?.isPlatformAdmin);

  const [contextDraft, setContextDraft] = useState(commonContext ?? '');
  const [savingContext, setSavingContext] = useState(false);

  useEffect(() => {
    setContextDraft(commonContext ?? '');
  }, [commonContext]);

  const [itemTypes, setItemTypes] = useState<ItemTypeListItem[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [stands, setStands] = useState<HermesStandOption[]>([]);
  const [standId, setStandId] = useState<string | null>(null);
  /** Настройка ещё с прежней схемы: адрес и ключ лежат в самом пространстве. */
  const [ownConnection, setOwnConnection] = useState(false);
  const [sources, setSources] = useState<HermesConfigSource[] | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const [taskDescriptionSource, setTaskDescriptionSource] = useState<string | undefined>();
  const [contextFieldSlugs, setContextFieldSlugs] = useState<string[]>([]);
  const [onMissingPath, setOnMissingPath] = useState<string>('skip');
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [configExists, setConfigExists] = useState(false);
  const [logs, setLogs] = useState<HermesEstimateLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);

  const loadLog = useCallback(
    async (itemTypeId: string) => {
      setLogLoading(true);
      try {
        setLogs(await getHermesLog(spaceId, itemTypeId));
      } catch {
        setLogs([]);
      } finally {
        setLogLoading(false);
      }
    },
    [spaceId],
  );

  useEffect(() => {
    listItemTypes(spaceId)
      .then(setItemTypes)
      .catch(() => setError('Не удалось загрузить типы записей'));
  }, [spaceId]);

  useEffect(() => {
    listSpaceHermesStands(spaceId)
      .then(setStands)
      .catch(() => setStands([]));
  }, [spaceId]);

  const resetForm = useCallback(() => {
    setBaseUrl('');
    setApiKey('');
    setApiKeyConfigured(false);
    setOwnConnection(false);
    // Новая настройка начинается со стенда по умолчанию — чтобы не выбирать
    // руками то же самое в каждом пространстве.
    setStandId(stands.find((s) => s.isDefault)?.id ?? null);
    setTaskDescriptionSource(undefined);
    setContextFieldSlugs([]);
    setOnMissingPath('skip');
    setRows([newRow()]);
    setConfigExists(false);
  }, [stands]);

  const loadForType = useCallback(
    async (itemTypeId: string) => {
      setLoading(true);
      setError(null);
      try {
        const itemType = await getItemType(spaceId, itemTypeId);
        setFields(itemType.fields);
        try {
          const cfg = await getHermesConfig(spaceId, itemTypeId);
          setConfigExists(true);
          setStandId(cfg.standId ?? null);
          setOwnConnection(!cfg.standId && (cfg.apiKeyConfigured || Boolean(cfg.baseUrl)));
          setBaseUrl(cfg.standId ? '' : cfg.baseUrl ?? '');
          setApiKey('');
          setApiKeyConfigured(cfg.apiKeyConfigured);
          setTaskDescriptionSource(cfg.inputMapping.taskDescriptionSource);
          setContextFieldSlugs(cfg.inputMapping.contextFieldSlugs ?? []);
          setOnMissingPath(cfg.settings.onMissingPath);
          setRows(
            cfg.resultMapping.fields.length
              ? cfg.resultMapping.fields.map((f) => ({ key: `row-${rowSeq++}`, ...f }))
              : [newRow()],
          );
          void loadLog(itemTypeId);
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            resetForm();
            setLogs([]);
          } else {
            throw err;
          }
        }
      } catch {
        setError('Не удалось загрузить конфигурацию');
        setFields([]);
      } finally {
        setLoading(false);
      }
    },
    [spaceId, resetForm, loadLog],
  );

  const handleSelectType = (id: string) => {
    setSelectedTypeId(id);
    void loadForType(id);
  };

  const textFields = useMemo(() => fields.filter((f) => TEXT_FAMILY.has(f.fieldType)), [fields]);
  const fieldTypeBySlug = useMemo(() => new Map(fields.map((f) => [f.slug, f.fieldType])), [fields]);
  const [rowIssues, setRowIssues] = useState<Record<string, string>>({});

  // Клиентская валидация строки результата (зеркало backend validateResultMapping):
  // пустые строки на сейве отбрасываются, остальные — проверяем тип поля ↔ transform.
  const rowProblem = useCallback(
    (r: ResultRow): string | null => {
      if (!r.activitiesFieldSlug) return null;
      const ft = fieldTypeBySlug.get(r.activitiesFieldSlug);
      if (!ft) return 'поле не найдено в этом типе';
      if (r.transform === 'number' && ft !== 'number' && ft !== 'computed') {
        return `нужно числовое поле (выбрано «${ft}»)`;
      }
      if (
        (r.transform === 'text' || r.transform === 'join_list' || r.transform === 'json') &&
        !TEXT_FAMILY.has(ft)
      ) {
        return `нужно текстовое поле (выбрано «${ft}»)`;
      }
      return null;
    },
    [fieldTypeBySlug],
  );

  const handleSave = async () => {
    if (!selectedTypeId) return;
    if (!taskDescriptionSource) {
      setError('Укажите поле-источник описания');
      return;
    }
    if (!configExists && !standId && !apiKey) {
      setError('Выберите стенд Гермеса');
      return;
    }
    // Не отправляем заведомо невалидный маппинг — иначе бэкенд отклонит ВЕСЬ сейв
    // (всё-или-ничего), и пользователь подумает, что сохранилось.
    const issues: Record<string, string> = {};
    for (const r of rows) {
      const p = rowProblem(r);
      if (p) issues[r.key] = p;
    }
    if (Object.keys(issues).length > 0) {
      setRowIssues(issues);
      const msg = `Маппинг не сохранён: ${Object.keys(issues).length} строк(и) с несовместимым полем — исправьте подсвеченные.`;
      setError(msg);
      message.error(msg);
      return;
    }
    setRowIssues({});
    setSaving(true);
    setError(null);
    try {
      await upsertHermesConfig(spaceId, selectedTypeId, {
        standId,
        baseUrl: standId ? null : baseUrl.trim() ? baseUrl.trim() : null,
        ...(apiKey ? { apiKey } : {}),
        inputMapping: {
          taskDescriptionSource,
          ...(contextFieldSlugs.length ? { contextFieldSlugs } : {}),
        },
        resultMapping: {
          fields: rows
            .filter((r) => r.activitiesFieldSlug)
            .map((r) => ({
              resultPath: r.resultPath,
              activitiesFieldSlug: r.activitiesFieldSlug,
              transform: r.transform,
            })),
        },
        settings: {
          onMissingPath: onMissingPath as never,
        },
      });
      message.success('Конфигурация сохранена');
      setApiKey('');
      void loadForType(selectedTypeId);
    } catch (err) {
      const details =
        err instanceof ApiError && err.details
          ? ` (${JSON.stringify(err.details)})`
          : '';
      const msg = (err instanceof ApiError ? err.message : 'Не удалось сохранить') + details;
      setError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const openCopy = () => {
    setCopySource(null);
    setCopyOpen(true);
    if (sources === null) {
      listHermesConfigSources()
        .then(setSources)
        .catch(() => setSources([]));
    }
  };

  const handleCopy = async () => {
    if (!selectedTypeId || !copySource) return;
    const [sourceSpaceId = '', sourceItemTypeId = ''] = copySource.split(':');
    setCopying(true);
    try {
      const res = await copyHermesConfigFrom(spaceId, selectedTypeId, { sourceSpaceId, sourceItemTypeId });
      setTaskDescriptionSource(res.inputMapping.taskDescriptionSource);
      setContextFieldSlugs(res.inputMapping.contextFieldSlugs ?? []);
      setOnMissingPath(res.settings.onMissingPath);
      setRows(
        res.resultMapping.fields.length
          ? res.resultMapping.fields.map((f) => ({ key: `row-${rowSeq++}`, ...f }))
          : [newRow()],
      );
      if (res.standId) {
        setStandId(res.standId);
        setOwnConnection(false);
      }
      setCopyOpen(false);
      if (res.skipped.length) {
        message.warning(`Подставлено. Пропущены поля, которых нет в этом типе: ${res.skipped.join(', ')}`);
      } else {
        message.success('Настройки подставлены — проверьте и сохраните');
      }
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : 'Не удалось скопировать настройки');
    } finally {
      setCopying(false);
    }
  };

  const handleTestKey = async () => {
    if (!selectedTypeId) return;
    setError(null);
    try {
      // Проверяем то, что выбрано в форме, а не то, что сохранено: иначе после
      // смены стенда кнопка отвечает про прежний.
      const res = await testHermesKey(
        spaceId,
        selectedTypeId,
        apiKey
          ? { apiKey, baseUrl: baseUrl || undefined }
          : standId
            ? { standId }
            : {},
      );
      if (res.ok) message.success(`Подключение работает (статус задачи: ${res.taskStatus})`);
      else message.error(`Подключение не работает: ${res.message}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось проверить ключ');
    }
  };

  const handleDelete = async () => {
    if (!selectedTypeId) return;
    setSaving(true);
    try {
      await deleteHermesConfig(spaceId, selectedTypeId);
      message.success('Конфигурация удалена');
      resetForm();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить');
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (key: string, patch: Partial<ResultRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Paragraph type="secondary">
        Оценка считается внешней системой Hermes по описанию задачи и
        записывается обратно в поля записи. Ключ X-API-Key хранится на уровне
        пространства. Настройте маппинг для каждого типа записей.
      </Typography.Paragraph>

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}

      <Card size="small" title="Общий контекст для оценки">
        <Typography.Paragraph type="secondary" style={{ marginBottom: 6 }}>
          Отправляется в Hermes преамбулой ко <b>всем</b> задачам этого
          пространства (стек, регламенты, общие ограничения). Добавляется перед
          описанием каждой записи.
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          maxLength={5000}
          showCount
          value={contextDraft}
          disabled={readOnly || savingContext}
          placeholder="Напр.: Стек — React + Fastify + PostgreSQL. Интеграции через Kafka. Соблюдаем 230-ФЗ…"
          onChange={(e) => setContextDraft(e.target.value)}
          data-testid="settings-hermes-context"
        />
        {!readOnly && (
          <Button
            size="small"
            type="primary"
            style={{ marginTop: 8 }}
            loading={savingContext}
            disabled={contextDraft === (commonContext ?? '')}
            onClick={async () => {
              setSavingContext(true);
              setError(null);
              try {
                const updated = await updateSpace(spaceId, {
                  hermesCommonContext: contextDraft.trim() ? contextDraft : null,
                });
                onCommonContextSaved(updated);
                message.success('Контекст сохранён');
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Не удалось сохранить контекст');
              } finally {
                setSavingContext(false);
              }
            }}
          >
            Сохранить контекст
          </Button>
        )}
      </Card>

      <Space>
        <Typography.Text>Тип записи:</Typography.Text>
        <Select
          style={{ minWidth: 240 }}
          placeholder="Выберите тип записи"
          value={selectedTypeId ?? undefined}
          onChange={handleSelectType}
          options={itemTypes.map((t) => ({ value: t.id, label: t.name }))}
          loading={loading}
        />
      </Space>

      {selectedTypeId && !loading && (
        <Card
          size="small"
          title="Стенд Гермеса"
          extra={
            <Button size="small" onClick={openCopy} disabled={readOnly}>
              Скопировать настройки из…
            </Button>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space wrap>
              <Select
                style={{ minWidth: 320 }}
                placeholder={stands.length ? 'Выберите стенд' : 'Стендов пока нет'}
                value={standId ?? undefined}
                disabled={readOnly || stands.length === 0}
                onChange={(value: string) => {
                  setStandId(value);
                  setOwnConnection(false);
                }}
                data-testid="hermes-stand-select"
                options={stands.map((st) => ({
                  value: st.id,
                  label: st.isDefault ? `${st.name} — по умолчанию` : st.name,
                }))}
              />
              <Button onClick={handleTestKey} disabled={readOnly || !configExists}>
                Проверить подключение
              </Button>
            </Space>
            {standId && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Адрес: {stands.find((st) => st.id === standId)?.baseUrl ?? 'из HERMES_BASE_URL'}
              </Typography.Text>
            )}
            {stands.length === 0 && (
              <Alert
                type="warning"
                showIcon
                message="Стенды Гермеса ещё не заведены"
                description={
                  isPlatformAdmin ? (
                    <span>
                      Заведите стенд в <Link to="/admin/settings">настройках платформы</Link> — после
                      этого он появится здесь и в остальных пространствах.
                    </span>
                  ) : (
                    'Попросите администратора платформы завести стенд в настройках платформы.'
                  )
                }
              />
            )}
            {ownConnection && (
              <>
                <Alert
                  type="info"
                  showIcon
                  message="Адрес и ключ заданы прямо в этом пространстве"
                  description="Так настраивали до появления общего списка стендов. Выберите стенд — он заменит собой эти поля."
                />
                <Input
                  addonBefore="Base URL"
                  type="url"
                  autoComplete="off"
                  placeholder="по умолчанию HERMES_BASE_URL"
                  value={baseUrl}
                  disabled={readOnly}
                  onChange={(e) => setBaseUrl(e.target.value)}
                />
                <Input.Password
                  addonBefore="X-API-Key"
                  autoComplete="new-password"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                  placeholder={apiKeyConfigured ? '•••• (задан, оставьте пустым чтобы не менять)' : 'hsk_...'}
                  value={apiKey}
                  disabled={readOnly}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </>
            )}
            {isPlatformAdmin && stands.length > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Список стендов правится в <Link to="/admin/settings">настройках платформы</Link>.
              </Typography.Text>
            )}
          </Space>
        </Card>
      )}

      <Modal
        open={copyOpen}
        title="Скопировать настройки оценки"
        onCancel={() => setCopyOpen(false)}
        onOk={() => void handleCopy()}
        okText="Подставить"
        cancelText="Отмена"
        confirmLoading={copying}
        okButtonProps={{ disabled: !copySource }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Маппинги полей подставятся в форму — сохранить их нужно будет самостоятельно.
            Поля, которых нет в этом типе записи, будут пропущены.
          </Typography.Text>
          <Select
            style={{ width: '100%' }}
            placeholder={sources === null ? 'Загрузка…' : 'Откуда копировать'}
            loading={sources === null}
            value={copySource ?? undefined}
            onChange={setCopySource}
            data-testid="hermes-copy-source"
            options={(sources ?? [])
              .filter((src) => !(src.spaceId === spaceId && src.itemTypeId === selectedTypeId))
              .map((src) => ({
                value: `${src.spaceId}:${src.itemTypeId}`,
                label: `${src.spaceName} → ${src.itemTypeName}${src.standName ? ` (${src.standName})` : ''}`,
              }))}
          />
        </Space>
      </Modal>

      {selectedTypeId && !loading && (
        <Card size="small" title="Что отправляем (описание задачи)">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space wrap>
              <Typography.Text>Источник описания:</Typography.Text>
              <Select
                style={{ minWidth: 240 }}
                placeholder="текстовое поле"
                value={taskDescriptionSource}
                disabled={readOnly}
                onChange={setTaskDescriptionSource}
                options={textFields.map((f) => ({ value: f.slug, label: `${f.name} (${f.fieldType})` }))}
              />
            </Space>
            <Space wrap>
              <Typography.Text>Контекстные поля:</Typography.Text>
              <Select
                mode="multiple"
                style={{ minWidth: 320 }}
                placeholder="доп. поля → строки «Имя: значение»"
                value={contextFieldSlugs}
                disabled={readOnly}
                onChange={setContextFieldSlugs}
                options={fields.map((f) => ({ value: f.slug, label: f.name }))}
              />
            </Space>
            <Space wrap>
              <Typography.Text>Нет поля в результате:</Typography.Text>
              <Select
                style={{ minWidth: 160 }}
                value={onMissingPath}
                disabled={readOnly}
                onChange={setOnMissingPath}
                options={HERMES_ON_MISSING_PATH.map((m) => ({
                  value: m,
                  label: m === 'skip' ? 'пропустить' : 'очистить',
                }))}
              />
            </Space>
          </Space>
        </Card>
      )}

      {selectedTypeId && !loading && (
        <Card
          size="small"
          title="Куда писать результат"
          extra={
            !readOnly && (
              <Space>
                <Button
                  size="small"
                  onClick={() =>
                    setRows(
                      TEMPLATE_ROWS.map((t) => ({ key: `row-${rowSeq++}`, activitiesFieldSlug: '', ...t })),
                    )
                  }
                >
                  Стандартный шаблон
                </Button>
                <Button icon={<PlusOutlined />} size="small" onClick={() => setRows((rs) => [...rs, newRow()])}>
                  Строка
                </Button>
              </Space>
            )
          }
        >
          <Table<ResultRow>
            dataSource={rows}
            pagination={false}
            rowKey="key"
            size="small"
            columns={[
              {
                title: 'Поле результата Hermes',
                dataIndex: 'resultPath',
                render: (_, row) => (
                  <Select
                    style={{ minWidth: 220 }}
                    value={row.resultPath}
                    disabled={readOnly}
                    onChange={(v) => updateRow(row.key, { resultPath: v })}
                    options={HERMES_RESULT_PATHS.map((p) => ({ value: p, label: p }))}
                  />
                ),
              },
              {
                title: 'Поле записи Lens',
                dataIndex: 'activitiesFieldSlug',
                render: (_, row) => {
                  const issue = rowIssues[row.key];
                  return (
                    <Tooltip title={issue ?? ''} open={issue ? undefined : false}>
                      <Select
                        style={{ minWidth: 200 }}
                        placeholder="поле"
                        status={issue ? 'error' : undefined}
                        value={row.activitiesFieldSlug || undefined}
                        disabled={readOnly}
                        onChange={(v) => {
                          updateRow(row.key, { activitiesFieldSlug: v });
                          setRowIssues((m) => {
                            if (!m[row.key]) return m;
                            const { [row.key]: _drop, ...rest } = m;
                            return rest;
                          });
                        }}
                        options={fields.map((f) => ({ value: f.slug, label: `${f.name} (${f.fieldType})` }))}
                      />
                    </Tooltip>
                  );
                },
              },
              {
                title: 'Преобразование',
                dataIndex: 'transform',
                render: (_, row) => (
                  <Select
                    style={{ minWidth: 140 }}
                    value={row.transform}
                    disabled={readOnly}
                    onChange={(v) => updateRow(row.key, { transform: v })}
                    options={HERMES_RESULT_TRANSFORMS.map((t) => ({ value: t, label: t }))}
                  />
                ),
              },
              {
                title: '',
                dataIndex: 'op',
                width: 48,
                render: (_, row) =>
                  !readOnly && (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setRows((rs) => rs.filter((r) => r.key !== row.key))}
                    />
                  ),
              },
            ]}
          />
        </Card>
      )}

      {selectedTypeId && !loading && configExists && (
        <Card
          size="small"
          title="История попыток"
          extra={
            <Button size="small" loading={logLoading} onClick={() => selectedTypeId && loadLog(selectedTypeId)}>
              Обновить
            </Button>
          }
        >
          <Table<HermesEstimateLogEntry>
            dataSource={logs}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            locale={{ emptyText: 'Оценок ещё не было' }}
            expandable={{
              expandedRowRender: (row) => {
                const req = (row.details as { request?: unknown } | null)?.request ?? null;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Typography.Text strong>Запрос в Hermes</Typography.Text>
                    <pre style={preStyle}>{JSON.stringify(req, null, 2)}</pre>
                    <Typography.Text strong>Ответ Hermes</Typography.Text>
                    <pre style={preStyle}>{JSON.stringify(row.resultRaw ?? null, null, 2)}</pre>
                    <Typography.Text strong>Детали</Typography.Text>
                    <pre style={preStyle}>{JSON.stringify(row.details ?? null, null, 2)}</pre>
                  </div>
                );
              },
            }}
            columns={[
              {
                title: 'Когда',
                dataIndex: 'requestedAt',
                width: 170,
                render: (v: string) => new Date(v).toLocaleString('ru-RU'),
              },
              {
                title: 'Статус',
                dataIndex: 'status',
                width: 120,
                render: (s: string) => {
                  const color =
                    s === 'completed' ? 'green' : s === 'running' || s === 'pending' ? 'blue' : 'red';
                  return <Tag color={color}>{s}</Tag>;
                },
              },
              { title: 'task_id', dataIndex: 'externalTaskId', ellipsis: true, render: (v: string | null) => v ?? '—' },
              {
                title: 'Детали',
                dataIndex: 'details',
                render: (d: unknown) => {
                  const det = d as { message?: string; code?: string; writtenFields?: string[] } | null;
                  if (!det) return '—';
                  if (det.message) return <Typography.Text type="danger">{det.code ? `[${det.code}] ` : ''}{det.message}</Typography.Text>;
                  if (det.writtenFields?.length) return <Typography.Text type="secondary">записано: {det.writtenFields.join(', ')}</Typography.Text>;
                  return '—';
                },
              },
            ]}
          />
        </Card>
      )}

      {selectedTypeId && !loading && !readOnly && (
        <Space>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Сохранить
          </Button>
          {configExists && (
            <Button danger loading={saving} onClick={handleDelete}>
              Удалить конфигурацию
            </Button>
          )}
        </Space>
      )}
    </Space>
  );
}
